import React, { useState } from "react";
import { DollarSign, Percent, Clock, Sparkles } from "lucide-react";
import { useSgdChfRate, convertSgdText } from "@/lib/currency";
import Chf from "@/components/Chf";

const PROJECTS_DATA = [
  {
    id: "sky11",
    name: "Sky@Eleven",
    district: "D11 Novena",
    type: "prime",
    gainNum: 786972,
    gainFormatted: "+S$786,972",
    retNum: 30.4,
    retFormatted: "+30.4%",
    holdingNum: 3.0,
    holdingPeriod: "3.0 Yrs",
    winRate: "98.4%",
    txCount: 124,
    highlight: "Highest dollar gain per seller (D11 Novena)",
  },
  {
    id: "wharf",
    name: "The Wharf Res.",
    district: "D9 River Valley",
    type: "prime",
    gainNum: 615904,
    gainFormatted: "+S$615,904",
    retNum: 37.0,
    retFormatted: "+37.0%",
    holdingNum: 2.6,
    holdingPeriod: "2.6 Yrs",
    winRate: "100%",
    txCount: 78,
    highlight: "Peak ROI return in sample (+37.0% in 2.6 Yrs)",
  },
  {
    id: "martin-place",
    name: "Martin Place",
    district: "D9 River Valley",
    type: "prime",
    gainNum: 534158,
    gainFormatted: "+S$534,158",
    retNum: 32.2,
    retFormatted: "+32.2%",
    holdingNum: 2.3,
    holdingPeriod: "2.3 Yrs",
    winRate: "98.9%",
    txCount: 92,
    highlight: "Fastest core luxury turnaround (2.3 Yrs)",
  },
  {
    id: "martin-modern",
    name: "Martin Modern",
    district: "D9 Robertson",
    type: "prime",
    gainNum: 430604,
    gainFormatted: "+S$430,604",
    retNum: 16.1,
    retFormatted: "+16.1%",
    holdingNum: 4.1,
    holdingPeriod: "4.1 Yrs",
    winRate: "91.4%",
    txCount: 81,
    highlight: "High-ticket modern completions (4.1 Yrs)",
  },
  {
    id: "clavon",
    name: "Clavon",
    district: "D5 Clementi",
    type: "nonprime",
    gainNum: 368155,
    gainFormatted: "+S$368,155",
    retNum: 25.5,
    retFormatted: "+25.5%",
    holdingNum: 3.9,
    holdingPeriod: "3.9 Yrs",
    winRate: "100%",
    txCount: 110,
    highlight: "100% profitable sub-sales (3.9 Yrs hold)",
  },
  {
    id: "parc-clematis",
    name: "Parc Clematis",
    district: "D5 Clementi",
    type: "nonprime",
    gainNum: 363542,
    gainFormatted: "+S$363,542",
    retNum: 26.3,
    retFormatted: "+26.3%",
    holdingNum: 3.9,
    holdingPeriod: "3.9 Yrs",
    winRate: "100%",
    txCount: 184,
    highlight: "High-volume mega-resort liquidity (3.9 Yrs hold)",
  },
  {
    id: "parc-esta",
    name: "Parc Esta",
    district: "D14 Eunos",
    type: "nonprime",
    gainNum: 285734,
    gainFormatted: "+S$285,734",
    retNum: 22.7,
    retFormatted: "+22.7%",
    holdingNum: 3.7,
    holdingPeriod: "3.7 Yrs",
    winRate: "100%",
    txCount: 142,
    highlight: "Doorstep MRT transit advantage (3.7 Yrs hold)",
  },
  {
    id: "treasure",
    name: "Treasure Tampines",
    district: "D18 Tampines",
    type: "nonprime",
    gainNum: 237110,
    gainFormatted: "+S$237,110",
    retNum: 21.8,
    retFormatted: "+21.8%",
    holdingNum: 3.7,
    holdingPeriod: "3.7 Yrs",
    winRate: "100%",
    txCount: 260,
    highlight: "Singapore's largest mega-development (3.7 Yrs hold)",
  },
];

export default function PrimeVsNonPrimeBars() {
  const chfRate = useSgdChfRate();
  const [metric, setMetric] = useState("profit"); // "profit" | "percent" | "holding"
  const [filterType, setFilterType] = useState("all"); // "all" | "prime" | "nonprime"
  const [hoveredProject, setHoveredProject] = useState(null);

  const filteredProjects = PROJECTS_DATA.filter((p) => {
    if (filterType === "all") return true;
    return p.type === filterType;
  });

  // Scales & Ticks
  const maxValues = {
    profit: 800000,
    percent: 40,
    holding: 5.0,
  };

  const yTicks = {
    profit: ["S$800k", "S$600k", "S$400k", "S$200k", "S$0"],
    percent: ["+40%", "+30%", "+20%", "+10%", "0%"],
    holding: ["5.0 Yrs", "4.0 Yrs", "3.0 Yrs", "2.0 Yrs", "0 Yrs"],
  };

  const currentMax = maxValues[metric];
  const currentTicks = metric === "profit" ? yTicks.profit.map((t) => convertSgdText(t, chfRate)) : yTicks[metric];

  // Helper to format values above bars
  const getBarValue = (item) => {
    if (metric === "profit") return convertSgdText(item.gainFormatted, chfRate);
    if (metric === "percent") return item.retFormatted;
    return `${item.holdingPeriod}`;
  };

  // Helper for height calculation
  const getBarHeightPx = (item) => {
    const maxHeightPx = 230; // maximum column height in px
    let val = item.gainNum;
    if (metric === "percent") val = item.retNum;
    if (metric === "holding") val = item.holdingNum;
    const pct = Math.min(1, Math.max(0.1, val / currentMax));
    return Math.round(pct * maxHeightPx);
  };

  return (
    <div className="bg-[#FAF8F5] border border-[#E7E2D6] rounded-3xl p-6 sm:p-8 lg:p-10 mb-16 shadow-xs">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 pb-6 border-b border-[#E7E2D6]">
        <div>
          <span className="text-xs font-mono uppercase text-[#C5A880] font-bold tracking-wider flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Sample Sub-Sale Analysis · Real Transaction Datasets
          </span>
          <h3 className="font-serif-display text-2xl sm:text-4xl text-[#0A1128] font-medium mt-1">
            How Prime vs. Non-Prime Markets Perform
          </h3>
          <p className="text-xs sm:text-sm text-slate-600 mt-2 max-w-2xl">
            Empirical comparison of realized capital gains, ROI returns, and average holding periods between Prime Core (D9, 10, 11, 15) and Suburban Hubs (D5, 14, 18).
          </p>
        </div>

        {/* Metric & Category Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Metric Toggle */}
          <div className="inline-flex items-center p-1 bg-white border border-[#E7E2D6] rounded-xl text-xs font-mono shadow-2xs">
            <button
              onClick={() => setMetric("profit")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                metric === "profit"
                  ? "bg-[#0A1128] text-[#C5A880] font-bold shadow-xs"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              <DollarSign className="w-3 h-3" />
              Dollar Profit
            </button>
            <button
              onClick={() => setMetric("percent")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                metric === "percent"
                  ? "bg-[#0A1128] text-[#C5A880] font-bold shadow-xs"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              <Percent className="w-3 h-3" />
              Return (%)
            </button>
            <button
              onClick={() => setMetric("holding")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                metric === "holding"
                  ? "bg-[#0A1128] text-[#C5A880] font-bold shadow-xs"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              <Clock className="w-3 h-3" />
              Holding Period
            </button>
          </div>

          {/* Market Filter */}
          <div className="inline-flex items-center p-1 bg-white border border-[#E7E2D6] rounded-xl text-xs font-mono shadow-2xs">
            <button
              onClick={() => setFilterType("all")}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                filterType === "all"
                  ? "bg-[#0A1128] text-white font-bold"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType("prime")}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                filterType === "prime"
                  ? "bg-[#0A1128] text-[#C5A880] font-bold"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              Prime Only
            </button>
            <button
              onClick={() => setFilterType("nonprime")}
              className={`px-3 py-1.5 rounded-lg transition-all ${
                filterType === "nonprime"
                  ? "bg-emerald-700 text-white font-bold"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              Non-Prime Only
            </button>
          </div>
        </div>
      </div>

      {/* TWO SUMMARY COMPARISON PILLARS */}
      <div className="grid md:grid-cols-2 gap-5 mb-10">
        {/* Prime Pillar */}
        <div className="bg-white border-2 border-[#C5A880]/50 rounded-2xl p-5 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#0A1128] border-2 border-[#C5A880]" />
              <span className="text-xs font-mono uppercase tracking-wider text-[#C5A880] font-bold">
                Prime Districts (D9, 10, 11, 15)
              </span>
            </div>
            <span className="text-[10px] font-mono font-bold bg-[#0A1128] text-[#C5A880] px-2 py-0.5 rounded-full">
              High Capital Quantum
            </span>
          </div>

          <div className="mt-4 flex items-baseline justify-between">
            <div>
              <div className="text-xs text-slate-500 font-mono">Sample Average Realized Profit</div>
              <div className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0A1128] mt-0.5">
                <Chf sgd={334560} prefix="+" />
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
                +21.2% Avg Return
              </span>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-mono text-slate-600">
            <span>Avg Holding Period: <strong className="text-[#0A1128]">2.8 Yrs</strong></span>
            <span>Peak Recorded: <strong className="text-[#C5A880]"><Chf sgd={6200000} compact prefix="+" /></strong></span>
            <span>Avg Ticket: <strong className="text-[#0A1128]"><Chf sgd={2230000} compact /></strong></span>
          </div>
        </div>

        {/* Non-Prime Pillar */}
        <div className="bg-white border-2 border-emerald-500/50 rounded-2xl p-5 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-emerald-600" />
              <span className="text-xs font-mono uppercase tracking-wider text-emerald-700 font-bold">
                Non-Prime Suburbs (D5, 14, 18, 19)
              </span>
            </div>
            <span className="text-[10px] font-mono font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full">
              100% Win Rate Track Record
            </span>
          </div>

          <div className="mt-4 flex items-baseline justify-between">
            <div>
              <div className="text-xs text-slate-500 font-mono">Sample Average Realized Profit</div>
              <div className="font-serif-display text-3xl sm:text-4xl font-bold text-emerald-700 mt-0.5">
                <Chf sgd={261153} prefix="+" />
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
                +21.8% Avg Return
              </span>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-mono text-slate-600">
            <span>Avg Holding Period: <strong className="text-emerald-700">3.9 Yrs</strong></span>
            <span>Win Rate: <strong className="text-emerald-700">100.0%</strong></span>
            <span>Avg Ticket: <strong className="text-[#0A1128]"><Chf sgd={1400000} compact /></strong></span>
          </div>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* THE CLEAN ARCHITECTURAL VERTICAL BAR GRAPH                           */}
      {/* ==================================================================== */}
      <div className="bg-white border border-[#E7E2D6] rounded-2xl p-5 sm:p-6 lg:p-8 shadow-xs">
        {/* Chart Top Legend & Scale Info */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-5 text-xs font-mono">
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-[2px] bg-[#0A1128] border-t-2 border-t-[#C5A880]" />
              <span className="text-[#0A1128] font-bold">Prime Districts (D9, D11)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-[2px] bg-emerald-700 border-t-2 border-t-emerald-400" />
              <span className="text-emerald-800 font-bold">Non-Prime Hubs (D5, D14, D18)</span>
            </div>
          </div>

          <div className="text-[11px] font-mono text-slate-500">
            {metric === "profit" && "Metric: Average Realized Gain (CHF)"}
            {metric === "percent" && "Metric: Capital Return (% Gain)"}
            {metric === "holding" && "Metric: Average Holding Duration (Years)"}
          </div>
        </div>

        {/* Vertical Bars Chart Stage with Y-Axis */}
        <div className="relative pt-4 pb-2">
          {/* Layout: Left Y-Axis Ticks + Right Chart Area */}
          <div className="flex items-stretch gap-3">
            {/* Y-Axis Column */}
            <div className="flex flex-col justify-between text-right text-[10px] font-mono text-slate-400 select-none pb-28 pt-7 w-12 sm:w-14 shrink-0">
              {currentTicks.map((tick, i) => (
                <span key={i} className="leading-none">{tick}</span>
              ))}
            </div>

            {/* Main Bar Chart Container */}
            <div className="relative flex-1">
              {/* Horizontal Background Grid Lines */}
              <div className="absolute inset-0 pb-28 pt-7 flex flex-col justify-between pointer-events-none">
                <div className="border-b border-dashed border-slate-200 w-full" />
                <div className="border-b border-dashed border-slate-200 w-full" />
                <div className="border-b border-dashed border-slate-200 w-full" />
                <div className="border-b border-dashed border-slate-200 w-full" />
                <div className="border-b border-slate-300 w-full" />
              </div>

              {/* Vertical Bars Flex Container */}
              <div className="relative z-10 flex items-end justify-around gap-2 sm:gap-4 h-[280px] px-1 sm:px-3">
                {filteredProjects.map((item) => {
                  const isPrime = item.type === "prime";
                  const isHovered = hoveredProject === item.id;
                  const barHeight = getBarHeightPx(item);

                  return (
                    <div
                      key={item.id}
                      onMouseEnter={() => setHoveredProject(item.id)}
                      onMouseLeave={() => setHoveredProject(null)}
                      className="flex-1 flex flex-col items-center justify-end h-full group cursor-pointer"
                    >
                      {/* Floating Value Label Above Bar */}
                      <div
                        className={`text-[11px] sm:text-xs font-mono font-bold mb-2.5 transition-all text-center whitespace-nowrap ${
                          isHovered ? "scale-110 -translate-y-0.5" : ""
                        } ${isPrime ? "text-[#0A1128]" : "text-emerald-700"}`}
                      >
                        {getBarValue(item)}
                      </div>

                      {/* Clean Flat Architectural Vertical Column Bar */}
                      <div className="w-full flex justify-center items-end">
                        <div
                          style={{ height: `${barHeight}px` }}
                          className={`w-9 sm:w-11 md:w-12 lg:w-14 rounded-t-[3px] border-t-2 transition-all duration-300 ${
                            isPrime
                              ? "bg-[#0A1128] border-t-[#C5A880] group-hover:bg-[#16234D]"
                              : "bg-emerald-700 border-t-emerald-400 group-hover:bg-emerald-600"
                          } ${isHovered ? "shadow-md brightness-110" : "shadow-xs"}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Baseline Divider */}
              <div className="w-full h-0.5 bg-slate-300 relative z-20" />

              {/* Project Labels Below X-Axis */}
              <div className="flex items-start justify-around gap-2 sm:gap-4 pt-3 px-1 sm:px-3">
                {filteredProjects.map((item) => {
                  const isPrime = item.type === "prime";
                  return (
                    <div
                      key={item.id}
                      onMouseEnter={() => setHoveredProject(item.id)}
                      onMouseLeave={() => setHoveredProject(null)}
                      className="flex-1 text-center cursor-pointer flex flex-col items-center"
                    >
                      {/* District Pill */}
                      <span
                        className={`inline-block text-[9px] sm:text-[10px] font-mono px-1.5 py-0.5 rounded font-bold mb-1 ${
                          isPrime
                            ? "bg-[#0A1128] text-[#C5A880]"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {item.district.split(" ")[0]}
                      </span>

                      {/* Project Name */}
                      <div className="font-serif-display text-[11px] sm:text-xs font-bold text-[#0A1128] truncate max-w-[85px] leading-tight group-hover:text-[#C5A880] transition-colors">
                        {item.name}
                      </div>

                      {/* PROMINENT HOLDING PERIOD BADGE */}
                      <div
                        className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border transition-colors ${
                          metric === "holding"
                            ? "bg-[#0A1128] text-[#C5A880] border-[#C5A880]"
                            : "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                        title={`Average holding period: ${item.holdingPeriod}`}
                      >
                        <Clock className="w-2.5 h-2.5 text-slate-500" />
                        <span>{item.holdingPeriod}</span>
                      </div>

                      {/* Win Rate Tag */}
                      <div className="text-[10px] font-mono text-slate-400 mt-1">
                        {item.winRate} win
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Interactive Tooltip Details Banner */}
          {hoveredProject && (
            <div className="mt-5 p-3.5 bg-[#FAF8F5] border border-[#C5A880] rounded-xl text-xs font-mono flex flex-wrap items-center justify-between gap-3 text-[#0A1128] animate-fadeIn shadow-2xs">
              {(() => {
                const p = PROJECTS_DATA.find((x) => x.id === hoveredProject);
                if (!p) return null;
                return (
                  <>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <span className="font-bold text-sm font-serif-display text-[#0A1128]">
                        {p.name} ({p.district}):
                      </span>
                      <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        {convertSgdText(p.gainFormatted, chfRate)} avg gain ({p.retFormatted})
                      </span>
                      <span className="text-[#0A1128] font-bold bg-slate-100 px-2 py-0.5 rounded border border-slate-200 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-[#C5A880]" />
                        Holding Period: {p.holdingPeriod}
                      </span>
                      <span className="text-slate-500">
                        ({p.txCount} sample sales · {p.winRate} profitable)
                      </span>
                    </div>
                    <span className="text-slate-600 hidden md:inline font-medium">✦ {p.highlight}</span>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Strategic Summary with Holding Period Insights */}
      <div className="mt-6 flex flex-wrap items-center justify-between text-xs text-slate-600 bg-white p-4 rounded-xl border border-[#E7E2D6] gap-3">
        <span className="font-medium text-[#0A1128] leading-relaxed">
          ★ <strong>Key Holding & Capital Dynamics:</strong> Prime Core developments deliver significantly higher absolute gains (+<Chf sgd={335000} compact /> average) across rapid turnaround cycles (average <strong>2.8 years holding period</strong>). In comparison, Non-Prime Suburban hubs provide an exceptional <strong>100% win-rate</strong> security profile with steady capital compounding over a typical <strong>3.9 years holding period</strong> tied to construction delivery.
        </span>
      </div>
    </div>
  );
}
